import React, { useState } from 'react';
import { Box, Button, InputAdornment, Popover, TextField, Tooltip, Typography } from '@mui/material';
import {
  BORDER, DARK, FIELD_SX, FLOAT_SHADOW, GHOST_BTN_SX, KIND_LABELS, KIND_ORDER, MODE_COLORS, MODE_HINTS, MODE_LABELS, MUTED, PERIOD_LABELS,
  PERM_HINTS_RULES, PRIMARY, PRIMARY_BTN_SX, PRIMARY_SOFT, SOFT, TRACK, minValue
} from './rulesConstants';
import { Panel, Segmented, modeOptions } from './RuleBits';
import { DocKind, RuleDef, RuleMode, RulePeriod } from './rulesTypes';

export interface MatrixCell { mode: RuleMode; value: string; period: RulePeriod }

interface Props {
  rules: RuleDef[];
  modes: RuleMode[];
  periods: RulePeriod[];
  draft: Record<string, MatrixCell>;
  keyOf: (rule: string, kind: DocKind) => string;
  isDirty: (key: string) => boolean;
  errorOf: (def: RuleDef, c: MatrixCell) => string | null;
  canEdit: boolean;
  saving: boolean;
  onPatch: (key: string, p: Partial<MatrixCell>) => void;
  onReset: (key: string) => void;
  /** Справа от легенды (переключатель вида) */
  toolbarRight?: React.ReactNode;
  /** Пояснение под матрицей */
  note?: string;
}

interface Editing { anchor: HTMLElement; def: RuleDef; kind: DocKind }

const fmtNum = (v: string) => {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) && v !== '' ? n.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : v || '-';
};

/**
 * Матрица правил: виды документов × правила на одном экране.
 * Плитка = режим + значение; клик открывает компактный редактор, легенда режимов подсвечивает плитки нужного режима.
 */
const RuleMatrix: React.FC<Props> = ({
  rules, modes, periods, draft, keyOf, isDirty, errorOf, canEdit, saving, onPatch, onReset, toolbarRight, note
}) => {
  const [focus, setFocus] = useState<RuleMode | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);

  const counts: Record<RuleMode, number> = { off: 0, observe: 0, enforce: 0 };
  rules.forEach(def => KIND_ORDER.forEach(kind => { const c = draft[keyOf(def.code, kind)]; if (c) counts[c.mode] += 1; }));

  const edKey = editing ? keyOf(editing.def.code, editing.kind) : '';
  const edCell = editing ? draft[edKey] : undefined;
  const edDirty = editing ? isDirty(edKey) : false;
  const edErr = editing && edCell && edDirty ? errorOf(editing.def, edCell) : null;
  const edOff = edCell?.mode === 'off';

  const tile = (def: RuleDef, kind: DocKind) => {
    const key = keyOf(def.code, kind);
    const c = draft[key];
    if (!c) return <Box key={key} />;
    const dirty = isDirty(key);
    const err = dirty ? errorOf(def, c) : null;
    const off = c.mode === 'off';
    const stripe = off ? '#cbd5e1' : MODE_COLORS[c.mode].color;
    const dim = focus !== null && c.mode !== focus;
    const closed = def.periodic && !off && Number(c.value) === 0;
    const interactive = canEdit && !saving;

    const body = (
      <Box
        component={interactive ? 'button' : 'div'}
        {...(interactive ? { type: 'button', onClick: (e: React.MouseEvent<HTMLElement>) => setEditing({ anchor: e.currentTarget, def, kind }) } : {})}
        aria-label={`${KIND_LABELS[kind]}: ${def.name}`}
        sx={{
          position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', gap: 0.35,
          width: '100%', minHeight: 92, m: 0, pl: 2, pr: 1.5, py: 1.25, textAlign: 'left', fontFamily: 'inherit',
          borderRadius: '10px', cursor: interactive ? 'pointer' : 'default',
          border: `1px solid ${err ? '#ef4444' : dirty ? PRIMARY : BORDER}`, bgcolor: dirty ? PRIMARY_SOFT : '#fff',
          boxShadow: `inset 4px 0 0 ${stripe}`, opacity: dim ? 0.3 : 1,
          transition: 'opacity .2s, border-color .15s, background-color .15s',
          '&:hover': interactive ? { borderColor: err ? '#ef4444' : dirty ? PRIMARY : '#94a3b8', bgcolor: dirty ? PRIMARY_SOFT : SOFT } : undefined,
          '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: 1 },
          '& .tile-edit': { opacity: 0, transition: 'opacity .15s' },
          '&:hover .tile-edit, &:focus-visible .tile-edit': { opacity: 1 }
        }}
      >
        <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, lineHeight: 1.3, color: off ? MUTED : MODE_COLORS[c.mode].color }}>
          {MODE_LABELS[c.mode]}
        </Typography>
        {off ? (
          <Typography component="span" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: '#cbd5e1' }}>-</Typography>
        ) : (
          <Typography component="span" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, color: err ? '#b91c1c' : DARK }}>
            {fmtNum(c.value)}
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: MUTED, ml: 0.6 }}>{def.unit}</Box>
          </Typography>
        )}
        <Typography component="span" sx={{ fontSize: 12, lineHeight: 1.3, color: err ? '#b91c1c' : MUTED }}>
          {err || (off ? 'не проверяется' : closed ? 'направление закрыто' : def.periodic ? PERIOD_LABELS[c.period] : 'на документ')}
        </Typography>
        {(dirty || interactive) && (
          <Typography
            className={dirty ? undefined : 'tile-edit'} component="span"
            sx={{ position: 'absolute', right: 10, top: 8, fontSize: 11.5, fontWeight: 600, color: PRIMARY }}
          >
            {dirty ? 'изменено' : 'изменить'}
          </Typography>
        )}
      </Box>
    );

    return canEdit ? <Box key={key}>{body}</Box> : (
      <Tooltip key={key} title={PERM_HINTS_RULES.networkReadOnly} arrow placement="top"><Box>{body}</Box></Tooltip>
    );
  };

  const cols = `minmax(150px, 190px) repeat(${Math.max(rules.length, 1)}, minmax(150px, 1fr))`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 12.5, color: MUTED, mr: 0.5 }}>Режимы:</Typography>
        {modes.map(m => {
          const sel = focus === m;
          return (
            <Tooltip key={m} title={`${MODE_HINTS[m]}. Нажмите, чтобы подсветить`} arrow disableInteractive>
              <Box
                component="button" type="button" aria-pressed={sel} onClick={() => setFocus(sel ? null : m)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 28, px: 1.25, m: 0, fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
                  borderRadius: '8px', cursor: 'pointer', border: `1px solid ${sel ? MODE_COLORS[m].color : '#cbd5e1'}`,
                  bgcolor: sel ? MODE_COLORS[m].bg : '#fff', color: sel ? MODE_COLORS[m].color : '#475569',
                  '&:hover': { bgcolor: sel ? MODE_COLORS[m].bg : SOFT }, '&:focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: 1 }
                }}
              >
                {MODE_LABELS[m]}
                <Box component="span" sx={{ color: sel ? 'inherit' : MUTED, fontWeight: 700 }}>{counts[m]}</Box>
              </Box>
            </Tooltip>
          );
        })}
        <Box sx={{ flex: 1 }} />
        {toolbarRight}
      </Box>

      <Panel sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: cols, gap: 1.25, minWidth: 190 + 150 * rules.length + 16, alignItems: 'stretch' }}>
          <Box />
          {rules.map(def => (
            <Tooltip key={def.code} title={def.description} arrow placement="top">
              <Box sx={{ px: 0.5, pb: 0.5, alignSelf: 'end' }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: DARK, lineHeight: 1.3 }}>{def.name}</Typography>
                <Typography sx={{ fontSize: 12, color: MUTED }}>{def.periodic ? `${def.unit}, за период` : def.unit}</Typography>
              </Box>
            </Tooltip>
          ))}
          {KIND_ORDER.map(kind => (
            <React.Fragment key={kind}>
              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: DARK }}>{KIND_LABELS[kind]}</Typography>
              </Box>
              {rules.map(def => tile(def, kind))}
            </React.Fragment>
          ))}
        </Box>
        {note && <Typography sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${TRACK}`, fontSize: 12.5, color: MUTED, lineHeight: 1.5 }}>{note}</Typography>}
      </Panel>

      <Popover
        open={Boolean(editing)} anchorEl={editing?.anchor} onClose={() => setEditing(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 330, borderRadius: '12px', border: `1px solid ${BORDER}`, boxShadow: FLOAT_SHADOW, backgroundImage: 'none' } } }}
      >
        {editing && edCell && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: DARK, lineHeight: 1.3 }}>{editing.def.name}</Typography>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>{KIND_LABELS[editing.kind]}</Typography>
            </Box>
            <Box>
              <Segmented
                value={edCell.mode} onChange={v => onPatch(edKey, { mode: v })}
                options={modeOptions(modes, MODE_LABELS, MODE_HINTS)}
              />
              <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.75 }}>{MODE_HINTS[edCell.mode]}</Typography>
            </Box>
            <TextField
              size="small" type="number" label="Значение" value={edOff ? '' : edCell.value} placeholder={edOff ? '-' : undefined}
              onChange={e => onPatch(edKey, { value: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') setEditing(null); }}
              disabled={edOff} error={Boolean(edErr)} autoFocus={!edOff}
              helperText={edErr || (editing.def.periodic && !edOff && Number(edCell.value) === 0 ? 'Направление закрыто' : ' ')}
              FormHelperTextProps={{ sx: { mx: 0.5, mt: 0.25, minHeight: 16, fontSize: 11.5, color: edErr ? undefined : MUTED } }}
              inputProps={{ min: minValue(editing.def), step: editing.def.integer ? 1 : 100 }}
              InputProps={{ endAdornment: <InputAdornment position="end"><Box component="span" sx={{ fontSize: 12.5, color: MUTED }}>{editing.def.unit}</Box></InputAdornment> }}
              sx={{ ...FIELD_SX, '& input': { fontSize: 14, fontWeight: 600 } }}
            />
            {editing.def.periodic && (
              <Segmented
                small value={edCell.period} onChange={v => onPatch(edKey, { period: v })} disabled={edOff}
                options={periods.map(p => ({ value: p, label: PERIOD_LABELS[p] }))}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5 }}>
              {edDirty && <Button size="small" onClick={() => onReset(edKey)} sx={{ ...GHOST_BTN_SX, mr: 'auto' }}>Вернуть</Button>}
              <Button size="small" variant="contained" disableElevation onClick={() => setEditing(null)} sx={PRIMARY_BTN_SX}>Готово</Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default RuleMatrix;
